// Pixi application, camera fit, and resize. The `stage` container is in map coordinates.
import { Application, Container } from 'pixi.js';

export interface RenderApp {
  app: Application;
  /** Root container in map units; scaled and centred to fit the viewport. */
  stage: Container;
  /** Called whenever the fit scale changes, so screen-sized things can compensate. */
  onScale: (scale: number) => void;
  destroy(): void;
}

export async function createRenderApp(
  parent: HTMLElement,
  mapSize: { w: number; h: number },
  background: number,
): Promise<RenderApp> {
  const app = new Application();
  await app.init({
    background,
    resizeTo: parent,
    antialias: true,
    autoDensity: true,
    resolution: Math.min(window.devicePixelRatio || 1, 2),
  });
  parent.appendChild(app.canvas);

  const stage = new Container();
  app.stage.addChild(stage);

  const result: RenderApp = {
    app,
    stage,
    onScale: () => undefined,
    destroy: () => app.destroy(true, { children: true }),
  };

  const fit = (): void => {
    const vw = app.screen.width;
    const vh = app.screen.height;
    // The board runs edge to edge; geography authored past the map rectangle bleeds
    // off screen rather than ending at a line.
    const scale = Math.min(vw / mapSize.w, vh / mapSize.h);
    // On a narrow screen the curve and shop occupy the bottom, so sit the board above
    // them instead of centring it behind them.
    const bottomUi = vw < 600 ? Math.min(vh * 0.3, 210) : 0;
    stage.scale.set(scale);
    stage.position.set(
      (vw - mapSize.w * scale) / 2,
      (vh - bottomUi - mapSize.h * scale) / 2 + (bottomUi > 0 ? 8 : 0),
    );
    result.onScale(scale);
  };
  fit();
  app.renderer.on('resize', fit);

  return result;
}
